import axios from "axios";
import { createInertiaApp, router } from "@inertiajs/svelte";
import PersistentLayout from "../Layouts/PersistentLayout.svelte";
import LoadingLayout from "../Layouts/LoadingLayout.svelte";
import EmbedLayout from "../Layouts/EmbedLayout.svelte";
import { sessionManager } from "../modules/global/session/SessionManager";
import { configureAxiosDefaults } from "../modules/global/api/apiClient";

// eslint-disable-next-line import/no-unresolved
import "../assets/stylesheets/application.css";

const csrfToken = document.querySelector("meta[name=csrf-token]").content;
axios.defaults.headers.common["X-CSRF-Token"] = csrfToken;

// Add bearer token to ALL axios requests via interceptor
axios.interceptors.request.use((config) => {
  const token = sessionManager.getToken();
  const sessionId = sessionManager.getSessionId();

  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  if (sessionId) {
    config.headers["X-Session-Id"] = sessionId;
  }

  return config;
});

// Add 401 response handling to global axios (token refresh + redirect to login)
configureAxiosDefaults();

// Add bearer token to Inertia page visits
router.on("before", (event) => {
  const token = sessionManager.getToken();
  const sessionId = sessionManager.getSessionId();

  if (token) {
    // Ensure headers object exists
    if (!event.detail.visit.headers) {
      event.detail.visit.headers = {};
    }
    event.detail.visit.headers["Authorization"] = `Bearer ${token}`;
    event.detail.visit.headers["X-Session-Id"] = sessionId;
  }
});

// Initialize session from page data if available
const initializeSession = () => {
  const sessionDataElement = document.getElementById("session-data");
  if (sessionDataElement) {
    try {
      const sessionData = JSON.parse(sessionDataElement.textContent || "{}");
      if (sessionData.sessionId && sessionData.apiToken) {
        sessionManager.initialize({
          userId: sessionData.userId,
          organisationId: sessionData.organisationId,
          organisationName: sessionData.organisationName,
          sessionId: sessionData.sessionId,
          apiToken: sessionData.apiToken,
          expiresAt: sessionData.expiresAt,
        });
      }
    } catch (error) {
      console.error("Failed to initialize session:", error);
    }
  }
};

// Initialize session on load
initializeSession();

// Expose sessionManager and axios globally for debugging
window.sessionManager = sessionManager;
window.axios = axios;

export const withVite = (name) => {
  const pages = import.meta.glob("../Pages/**/**/**/**/*.svelte");
  // eslint-disable-next-line no-restricted-syntax
  for (const path in pages) {
    if (path.endsWith(`${name.replace(".", "/")}.svelte`)) {
      return typeof pages[path] === "function" ? pages[path]() : pages[path];
    }
  }

  throw new Error(`Page not found: ${name}`);
};

export const doesNotRequireLayout = (name) => {
  const arr = ["login", "onboarding", "Onboarding", "Deactivated", "magic_status",
    "Invitations/Welcome", "/min"];

  return arr.filter((e) => name.includes(e)).length > 0;
};

// Check if page should use embed layout
export const shouldUseEmbedLayout = (name) => {
  // Inertia page names use dot notation, e.g., "Audits.embed"
  return name.toLowerCase().includes("embed");
};

createInertiaApp({
  progress: {
    showSpinner: true,
    includeCSS: true,
    color: "#29d",
  },
  resolve: (name) => {
    // Determine which layout to use
    let layout = PersistentLayout;
    
    if (shouldUseEmbedLayout(name)) {
      layout = EmbedLayout;
    } else if (doesNotRequireLayout(name)) {
      layout = LoadingLayout;
    }
    
    return withVite(name).then((page) => ({ layout, ...page }));
  },
  setup({ el, App, props }) {
    new App({ target: el, props });
  },
});
